import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchCustomerFeedback as clientFetchCustomerFeedback,
  fetchReports as clientFetchReports,
  fetchUserActivityReport as clientFetchUserActivityReport,
  fetchUserStatusReports as clientFetchUserStatusReports,
} from 'src/services/client';
import {
  ICustomerFeedbackRow,
  IServiceRequestReportRow,
  IUserActivityLogRow,
  IUserStatusReportRow,
  ReportRow,
} from 'src/SharedCustomHooks/UseReportingAPI/apiContracts';
import {
  IUserActivityMappedRow,
  mapUserActivityRow,
} from 'src/SharedCustomHooks/UseReportingAPI/helpers';
import { StandardReportParams } from 'src/pages/Reporting/ReportingFormTypes';

interface UseReportingAPIState {
  reports: ReportRow[];
  feedbackReport: ICustomerFeedbackRow[];
  isGeneratingReport: boolean;
  error: string | null;
}

export interface UseReportingAPIReturn {
  reports: ReportRow[];
  feedbackReport: ICustomerFeedbackRow[];
  isGeneratingReport: boolean;
  reportingError: string | null;
  fetchStandardReport: (params: StandardReportParams) => Promise<void>;
  fetchUserActivityReport: (params: StandardReportParams) => Promise<void>;
  fetchUserStatusReport: (propertyIds: string) => Promise<void>;
  fetchCustomerFeedbackReport: (params: StandardReportParams) => Promise<void>;
  clearReportingState: () => void;
}

const INITIAL_STATE: UseReportingAPIState = {
  reports: [],
  feedbackReport: [],
  isGeneratingReport: false,
  error: null,
};

export const useReportingAPI = (): UseReportingAPIReturn => {
  const [state, setState] = useState<UseReportingAPIState>(INITIAL_STATE);

  const setIsGenerating = (isGenerating: boolean): void => {
    setState((prev: UseReportingAPIState) => ({
      ...prev,
      isGeneratingReport: isGenerating,
    }));
  };

  const setReports = (reports: ReportRow[]): void => {
    setState((prev: UseReportingAPIState) => ({ ...prev, reports }));
  };

  const setFeedbackReport = (feedbackReport: ICustomerFeedbackRow[]): void => {
    setState((prev: UseReportingAPIState) => ({ ...prev, feedbackReport }));
  };

  const fetchStandardReport = async (
    params: StandardReportParams
  ): Promise<void> => {
    try {
      setIsGenerating(true);

      const response: any = await clientFetchReports(params);

      if (response.ok || response.status === 200) {
        const responseJson: IServiceRequestReportRow[] = await response.json();
        const hasRows: boolean = !!responseJson && responseJson.length > 0;

        if (!hasRows) {
          toast.success('No reports found.', {
            autoClose: 5000,
            position: 'top-center',
          });
        }

        setReports(responseJson || []);
      } else {
        setReports([]);
      }
    } catch (error: any) {
      console.error('Error fetching standard report:', error);
      toast.error(' 🚫 Something went wrong, please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchUserActivityReport = async (
    params: StandardReportParams
  ): Promise<void> => {
    try {
      setIsGenerating(true);

      const response: any = await clientFetchUserActivityReport(params);

      if (response.ok || response.status === 200) {
        const responseJson: IUserActivityLogRow[] = await response.json();
        const hasRows: boolean = !!responseJson && responseJson.length > 0;

        if (hasRows) {
          const mappedRows: IUserActivityMappedRow[] = responseJson.map(
            (row: IUserActivityLogRow) => mapUserActivityRow(row)
          );

          setReports(mappedRows);
        } else {
          toast.success('No reports found.', {
            autoClose: 5000,
            position: 'top-center',
          });

          setReports([]);
        }
      } else {
        setReports([]);
      }
    } catch (error: any) {
      console.error('Error fetching user activity report:', error);
      toast.error(' 🚫 Something went wrong, please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchUserStatusReport = async (propertyIds: string): Promise<void> => {
    try {
      setIsGenerating(true);

      const response: any = await clientFetchUserStatusReports(propertyIds);

      if (response.ok || response.status === 200) {
        const responseJson: IUserStatusReportRow[] = await response.json();
        const hasRows: boolean = !!responseJson && responseJson.length > 0;

        if (hasRows) {
          setReports(responseJson);
        } else {
          toast.success('No reports found.', {
            autoClose: 5000,
            position: 'top-center',
          });

          setReports([]);
        }
      } else {
        setReports([]);
      }
    } catch (error: any) {
      console.error('Error fetching user status report:', error);
      toast.error(' 🚫 Something went wrong, please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchCustomerFeedbackReport = async (
    params: StandardReportParams
  ): Promise<void> => {
    try {
      setIsGenerating(true);

      const response: any = await clientFetchCustomerFeedback(params);

      if (response.ok || response.status === 200) {
        const responseJson: ICustomerFeedbackRow[] = await response.json();
        const hasRows: boolean = !!responseJson && responseJson.length > 0;

        if (!hasRows) {
          toast.success('No reports found.', {
            autoClose: 5000,
            position: 'top-center',
          });
        }

        setFeedbackReport(responseJson || []);
      } else {
        setFeedbackReport([]);
      }
    } catch (error: any) {
      console.error('Error fetching customer feedback report:', error);
      toast.error(' 🚫 Something went wrong, please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const clearReportingState = (): void => {
    setState(INITIAL_STATE);
  };

  return {
    reports: state.reports,
    feedbackReport: state.feedbackReport,
    isGeneratingReport: state.isGeneratingReport,
    reportingError: state.error,
    fetchStandardReport,
    fetchUserActivityReport,
    fetchUserStatusReport,
    fetchCustomerFeedbackReport,
    clearReportingState,
  };
};
